import React, { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Area, AreaChart, ResponsiveContainer, Tooltip, YAxis } from 'recharts';
import { TrendingDown, TrendingUp, Minus } from 'lucide-react';

// İK Zekâsı panelindeki eğilim grafiği.
//
// ═══════════ NEDEN TEK SAYI DEĞİL EĞİLİM ═══════════
// Panelde eskiden "Aktif Çalışan Sayısı" tek bir rakam olarak duruyordu. O sayı zaten
// panelin en üstündeki özet kutularında var ve tek başına bir şey SÖYLEMİYOR — 189 kişi
// olmak iyi de olabilir kötü de. Bir KPI'ın anlamı YÖNÜNDEDİR. Metrik ucu her metrik için
// aylık seri döndürdüğünden (HaMetricsDataAccess snapshot'ları PeriodStart'a göre ARTAN
// sırada verir) eğilim çizmek için ek bir uca gerek yok.
//
// ═══════════ DEĞİŞİMİN İŞARETİ NÖTR DEĞİL ═══════════
// Devir oranında ARTIŞ kötüdür, düşüş iyidir. Renk bunu yansıtır; "yeşil = arttı" demek
// okuyanı yanıltırdı. Yön yorumunun metriğe bağlı olduğunu `$lowerIsBetter` ile açıkça
// alıyoruz, varsayımla değil.

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Head = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 2px;
`;

const Label = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Value = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xl};
  font-weight: ${({ theme }) => theme.metrics.font.weight.extraBold};
  line-height: 1;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Delta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme, $tone }) =>
    $tone === 'good'
      ? theme.colors.secondary.dark
      : $tone === 'bad'
        ? theme.colors.auxiliar.danger
        : theme.colors.text.muted};
`;

// Yükseklik ResponsiveContainer'a SAYI olarak veriliyor (aşağıda), buraya değil.
// `height: 100%` + `ResponsiveContainer height="100%"` ikilisi ebeveynin ölçülmesine bağlıdır
// ve ölçüm 0 döndüğünde grafik SESSİZCE hiç çizilmez. Depodaki çalışan örnek
// (AgeDistributionCard) de sayısal yükseklik kullanıyor; aynı desen izleniyor.
const ChartBox = styled.div`
  width: 100%;
  margin-top: 6px;
`;

const TooltipBox = styled.div`
  background: ${({ theme }) => theme.colors.surface.card};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: ${({ theme }) => theme.metrics.radius.small};
  padding: 6px 10px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.primary};
  box-shadow: 0 2px 8px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.12);
`;

const CHART_HEIGHT = 96;

// Oran metrikleri 0-1 aralığında gelir; kullanıcı yüzde bekler.
const asPercent = (v, locale) =>
  new Intl.NumberFormat(locale, { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(v);

export default function HaTrendChart({
  label,
  cells,
  locale,
  lowerIsBetter = true,
  gradientId = 'ha-trend-fill',
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  const points = useMemo(
    () =>
      (cells || [])
        .filter((c) => c && c.value !== null && c.value !== undefined)
        .map((c) => ({ period: c.periodStart, value: Number(c.value) })),
    [cells]
  );

  // İki noktadan az seride "eğilim" diye bir şey yoktur — düz bir çizgi çizip yön ima etmek
  // yanıltıcı olurdu.
  if (points.length < 2) return null;

  const son = points[points.length - 1].value;
  const onceki = points[points.length - 2].value;
  const fark = son - onceki;
  // Eşik UYDURULMUYOR: değişim tam olarak sıfırsa nötr, değilse yönüne göre.
  const tone = fark === 0 ? 'flat' : (fark < 0) === lowerIsBetter ? 'good' : 'bad';
  const TrendIcon = fark === 0 ? Minus : fark > 0 ? TrendingUp : TrendingDown;

  return (
    <Wrap data-role="ha-trend-chart">
      <Label>{label}</Label>
      <Head>
        <Value>{asPercent(son, locale)}</Value>
        <Delta $tone={tone}>
          <TrendIcon size={13} />
          {asPercent(Math.abs(fark), locale)}
          {' '}
          {t('dashboard.haPanel.vsPrevious')}
        </Delta>
      </Head>

      <ChartBox>
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <AreaChart data={points} margin={{ top: 4, right: 2, bottom: 0, left: 2 }}>
            <defs>
              {/* Çizginin altı marka rengiyle taranır ve aşağı doğru ŞEFFAFA iner —
                  kartın zeminini boğmadan hacim hissi verir. */}
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={theme.colors.primary.main} stopOpacity={0.28} />
                <stop offset="100%" stopColor={theme.colors.secondary.main} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            {/* Eksen GİZLİ ama alan HESAPLI: `domain` otomatik olsaydı küçük dalgalanmalar
                dramatik görünürdü. Yine de sabit bir aralık dayatmıyoruz — veri neyse o. */}
            <YAxis hide domain={['auto', 'auto']} />
            <Tooltip
              cursor={{ stroke: theme.colors.primary.light, strokeWidth: 1 }}
              content={({ active, payload }) =>
                active && payload && payload.length ? (
                  <TooltipBox>
                    {new Date(payload[0].payload.period).toLocaleDateString(locale, {
                      year: 'numeric', month: 'short',
                    })}
                    {' · '}
                    {asPercent(payload[0].value, locale)}
                  </TooltipBox>
                ) : null
              }
            />
            <Area
              type="monotone"          /* eğri — köşeli kırıklar değil */
              dataKey="value"
              stroke={theme.colors.primary.main}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 3, strokeWidth: 0, fill: theme.colors.primary.main }}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartBox>
    </Wrap>
  );
}
